/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import shaka from "shaka-player/dist/shaka-player.compiled";

import { Log } from "../../logging";
import { TPlaybackDeviceSupport } from "../types";

export class ShakaPlayerDeviceSupport {
  private static shakaPlayerDeviceSupportIns: ShakaPlayerDeviceSupport | null =
    null;
  private deviceSupport: TPlaybackDeviceSupport = {};

  private constructor() {}

  public static getInstance() {
    if (!ShakaPlayerDeviceSupport.shakaPlayerDeviceSupportIns) {
      ShakaPlayerDeviceSupport.shakaPlayerDeviceSupportIns =
        new ShakaPlayerDeviceSupport();
    }
    return ShakaPlayerDeviceSupport.shakaPlayerDeviceSupportIns;
  }

  public async fetchPlaybackDeviceSupport(): Promise<void> {
    this.deviceSupport = {};
    try {
      const support = await shaka.Player.probeSupport();

      // 1. Parse media support into structured format
      // Structure: { video: { mp4: { codecs: [...], eotf: [...] } }, audio: {...}, ... }
      if (support.media !== null && support.media !== undefined) {
        const mediaSupport: Record<
          string,
          Record<string, Record<string, string[]>>
        > = {};

        Object.entries(support.media).forEach(
          ([mimeType, isSupported]): void => {
            if (isSupported === false) {
              return;
            }

            // Parse mime type: "video/mp4; codecs="hvc1.2.4.L153.B0"; eotf="smpte2084""
            const [baseType, ...params] = mimeType
              .split(";")
              .map((s) => s.trim());
            const [category, format] = baseType.split("/");

            if (category === undefined || format === undefined) {
              return;
            }

            // Initialize nested structure if needed
            if (mediaSupport[category] === undefined) {
              mediaSupport[category] = {};
            }
            if (mediaSupport[category][format] === undefined) {
              mediaSupport[category][format] = {};
            }

            // Parse parameters like codecs="value" and eotf="value"
            params.forEach((param): void => {
              const match = param.match(/^(\w+)="([^"]+)"$/);
              if (
                match !== null &&
                match[1] !== undefined &&
                match[2] !== undefined
              ) {
                const paramKey = match[1];
                const paramValue = match[2];

                if (mediaSupport[category][format][paramKey] === undefined) {
                  mediaSupport[category][format][paramKey] = [];
                }

                // Avoid duplicate values
                if (
                  !mediaSupport[category][format][paramKey].includes(paramValue)
                ) {
                  mediaSupport[category][format][paramKey].push(paramValue);
                }
              }
            });
          }
        );

        this.deviceSupport.media = mediaSupport;
      }

      // 2. DRM support from Shaka probeSupport
      if (support.drm !== null && support.drm !== undefined) {
        this.deviceSupport.drm = Object.fromEntries(
          Object.entries(support.drm)
            .filter(([_, drmConfig]): boolean => drmConfig !== null)
            .map(([drmKey, drmConfig]): [string, Record<string, unknown[]>] => {
              // For each DRM object, filter out keys with false values
              // and wrap non-array values in arrays
              const filteredConfig = Object.fromEntries(
                Object.entries(drmConfig as Record<string, unknown>)
                  .filter(([_, value]): boolean => value !== false)
                  .map(([key, value]): [string, unknown[]] => {
                    const arrayValue = Array.isArray(value) ? value : [value];
                    return [key, arrayValue];
                  })
              );
              return [drmKey, filteredConfig];
            })
        );
      } else {
        this.deviceSupport.drm = {};
      }
    } catch (error: unknown) {
      Log.e("checkSupport()", String(error));
    }
  }

  public getPlaybackDeviceSupport(): TPlaybackDeviceSupport {
    return this.deviceSupport;
  }
}
